import React, { useState, useMemo, useEffect } from 'react';
import { LeftHandFinger, HandPart, ALL_LEFT_FINGERS, getFingerDisplayName, FingerType } from '../types';
import { 
  IMAGE_URLS, 
  FINGER_KEY_MAP, 
  KEY_FINGER_MAP,
  HAND_PART_SVG_PATHS,
  FINGER_POSITION_ADJUSTMENTS,
  DEBUG_FINGER_AREAS,
  LETTER_POSITION_ADJUSTMENTS,
  LETTER_FONT_SETTINGS,
  DEBUG_LETTER_POSITIONS
} from '../constants';

// Common image style (bypasses Tailwind) - FORCED EXACT SIZING
const imageLayerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '507px',  // FORCED to match container
  height: '580px', // FORCED to match container
  objectFit: 'fill', // CHANGED from 'contain' to 'fill' for exact sizing
  pointerEvents: 'none',
  display: 'block'
};

// ENHANCED Props interface for real integration with KeyboardHero system
interface LeftHandPickerProps {
  selectedFingers?: FingerType[];
  onChange?: (fingers: FingerType[]) => void;
  disabled?: boolean;
  className?: string;
  mode?: 'active' | 'demonstration';
  externalFingerData?: LeftHandFinger[];
  externalHandData?: 'full' | 'none';
  onModeChange?: (mode: 'active' | 'demonstration') => void;
}

const LeftHandPicker: React.FC<LeftHandPickerProps> = ({
  selectedFingers: externalSelectedFingers = [],
  onChange,
  disabled = false,
  className = '',
  mode = 'active',
  externalFingerData = [],
  externalHandData = 'none'
}) => {
  const [hoveredPart, setHoveredPart] = useState<HandPart | null>(null);
  const [selectedFingers, setSelectedFingers] = useState<Set<LeftHandFinger>>(new Set());
  
  const isHandFloating = useMemo(() => {
    const hasHoverInteraction = hoveredPart !== null;
    const hasSelectedFingers = selectedFingers.size > 0;
    const isDisabledState = disabled;
    const hasExternalDemoData = mode === 'demonstration' && (externalFingerData.length > 0 || externalHandData === 'full');
    return !hasHoverInteraction && !hasSelectedFingers && !isDisabledState && !hasExternalDemoData;
  }, [hoveredPart, selectedFingers, disabled, mode, externalFingerData, externalHandData]);
  
  useEffect(() => {
    if (mode === 'active') {
      const leftHandFingers = externalSelectedFingers.filter(f => f.startsWith('left')) as LeftHandFinger[];
      setSelectedFingers(new Set(leftHandFingers));
    }
  }, [externalSelectedFingers, mode]);
  
  useEffect(() => {
    if (mode === 'demonstration') {
      let demonstrationFingers = new Set<LeftHandFinger>();
      if (externalHandData === 'full') {
        demonstrationFingers = new Set(ALL_LEFT_FINGERS);
      } else if (externalFingerData.length > 0) {
        demonstrationFingers = new Set(externalFingerData);
      }
      setSelectedFingers(demonstrationFingers);
    }
  }, [mode, externalFingerData, externalHandData]);

  const areAllFingersSelected = useMemo(() => selectedFingers.size === ALL_LEFT_FINGERS.length, [selectedFingers]);
  
  const fireFingerSelectionChange = (newSelectedFingers: Set<LeftHandFinger>) => {
    if (onChange) onChange(Array.from(newSelectedFingers) as FingerType[]);
  };

  const handlePartClick = (part: HandPart) => {
    if (disabled || mode === 'demonstration') return;
    
    let newSelectedFingers: Set<LeftHandFinger>;
    if (part === HandPart.PALM) {
      newSelectedFingers = areAllFingersSelected ? new Set() : new Set(ALL_LEFT_FINGERS);
    } else {
      newSelectedFingers = new Set(selectedFingers);
      const fingerValue = part as LeftHandFinger;
      if (areAllFingersSelected) {
        newSelectedFingers = new Set([fingerValue]);
      } else {
        newSelectedFingers.has(fingerValue) ? newSelectedFingers.delete(fingerValue) : newSelectedFingers.add(fingerValue);
      }
    }
    setSelectedFingers(newSelectedFingers);
    fireFingerSelectionChange(newSelectedFingers);
  };

  const handlePartMouseEnter = (part: HandPart) => {
    if (mode !== 'demonstration') setHoveredPart(part);
  };

  const handlePartMouseLeave = () => {
    if (mode !== 'demonstration') setHoveredPart(null);
  };
  
  const isFingerActive = (finger: LeftHandFinger) => selectedFingers.has(finger) || hoveredPart === finger;
  const isPalmActive = () => areAllFingersSelected || hoveredPart === HandPart.PALM;
  
  const activeKeys = useMemo(() => {
    const keys = new Set<string>();
    selectedFingers.forEach(finger => FINGER_KEY_MAP[finger].forEach(key => keys.add(key)));
    if (hoveredPart && hoveredPart !== HandPart.PALM) {
      const hoveredFinger = hoveredPart as LeftHandFinger;
      FINGER_KEY_MAP[hoveredFinger].forEach(key => keys.add(key));
    }
    if(hoveredPart === HandPart.PALM){
      ALL_LEFT_FINGERS.forEach(finger => FINGER_KEY_MAP[finger].forEach(key => keys.add(key)));
    }
    return keys;
  }, [selectedFingers, hoveredPart]);

  return (
    <div 
      style={{
        position: 'relative',
        width: '507px',
        height: '580px',
        userSelect: 'none'
      }}
      className={className}
    >
      {/* Debug Panel */}
      {(DEBUG_FINGER_AREAS || DEBUG_LETTER_POSITIONS) && (
        <div style={{position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.8)', color: 'white', padding: '8px', borderRadius: '4px', fontSize: '12px', zIndex: 50}}>
          <div style={{fontWeight: 'bold', marginBottom: '4px'}}>🐛 DEBUG MODE</div>
          {DEBUG_FINGER_AREAS && <div style={{color: '#fca5a5'}}>🔴 Finger Areas: ON</div>}
          {DEBUG_LETTER_POSITIONS && <div style={{color: '#86efac'}}>🟢 Letter Positions: ON</div>}
        </div>
      )}
      
      {/* Layer 1: Keyboard Base */}
      <img 
        src={IMAGE_URLS.KEYBOARD_BASE} 
        alt="Keyboard Base" 
        style={imageLayerStyle}
      />

      {/* Layer 2: Active Keys */}
      {Object.keys(KEY_FINGER_MAP).map((key) => (
        <img
          key={key}
          src={IMAGE_URLS.KEYS[key as keyof typeof IMAGE_URLS.KEYS]}
          alt={`${key} active`}
          style={{
            ...imageLayerStyle,
            opacity: activeKeys.has(key) ? 1 : 0,
            transition: 'opacity 200ms'
          }}
        />
      ))}
      
      {/* Layer 3: Key Letters */}
      {Object.entries(LETTER_POSITION_ADJUSTMENTS).map(([letter, pos]) => {
        const offsetX = pos.offsetX || '0px';
        const offsetY = pos.offsetY || '0px';
        return (
          <span
            key={letter}
            style={{
              position: 'absolute',
              top: pos.top,
              left: pos.left,
              transform: `translate(-50%, -50%) translate(${offsetX}, ${offsetY})`,
              pointerEvents: 'none',
              userSelect: 'none',
              fontSize: LETTER_FONT_SETTINGS.fontSize,
              fontFamily: LETTER_FONT_SETTINGS.fontFamily,
              fontWeight: LETTER_FONT_SETTINGS.fontWeight,
              color: LETTER_FONT_SETTINGS.color,
              textShadow: LETTER_FONT_SETTINGS.textShadow,
              letterSpacing: LETTER_FONT_SETTINGS.letterSpacing,
              ...(DEBUG_LETTER_POSITIONS ? {
                backgroundColor: 'rgba(0, 255, 0, 0.3)',
                border: '1px solid lime',
                padding: '2px 4px',
                borderRadius: '3px',
              } : {}),
            }}
          >
            {letter === 'SPACE' ? '' : letter}
          </span>
        );
      })}

      {/* Animated Hand Container */}
      <div 
        className={isHandFloating ? 'animate-hand-soaring' : ''}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          transition: 'transform 500ms ease-out',
          // @ts-ignore - CSS variables
          '--float-duration': '4.5s',
          '--float-amplitude': '8px',
          '--float-rotation': '0.5deg',
          '--float-scale': '1.01',
        }}
      >
        {/* Layer 4: Inactive Hand */}
        <img 
          src={IMAGE_URLS.LEFT_PALM_INACTIVE} 
          alt="Inactive Hand" 
          style={imageLayerStyle}
        />
      
        {/* Layer 5: Active Palm */}
        {isPalmActive() && (
          <img 
            src={IMAGE_URLS.LEFT_PALM_ACTIVE} 
            alt="Active Palm" 
            style={{
              ...imageLayerStyle,
              transition: 'opacity 200ms'
            }}
          />
        )}
        
        {/* Layer 5: Active Fingers */}
        {!isPalmActive() && ALL_LEFT_FINGERS.map((finger) => {
          const getImageKey = (finger: LeftHandFinger): keyof typeof IMAGE_URLS => {
            switch (finger) {
              case 'leftPinky': return 'LEFT_PINKY_ACTIVE';
              case 'leftRing': return 'LEFT_RING_ACTIVE';
              case 'leftMiddle': return 'LEFT_MIDDLE_ACTIVE';
              case 'leftPointing': return 'LEFT_INDEX_ACTIVE';
              case 'leftThumb': return 'LEFT_THUMB_ACTIVE';
              default: return 'LEFT_PINKY_ACTIVE';
            }
          };
          
          const imageKey = getImageKey(finger);
          return (
            <img
              key={finger}
              src={IMAGE_URLS[imageKey] as string}
              alt={`${getFingerDisplayName(finger)} active`}
              style={{
                ...imageLayerStyle,
                opacity: isFingerActive(finger) ? 1 : 0,
                transition: 'opacity 200ms'
              }}
            />
          );
        })}

        {/* Layer 6: SVG Hotspots */}
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%'
          }}
          viewBox="0 0 1015 1159"
          fill="transparent"
          xmlns="http://www.w3.org/2000/svg"
        >
          {Object.entries(HAND_PART_SVG_PATHS).map(([part, pathData]) => {
            const adjustments = FINGER_POSITION_ADJUSTMENTS[part as HandPart];
            const transform = `translate(${adjustments.translateX}, ${adjustments.translateY}) scale(${adjustments.scaleX || 1}, ${adjustments.scaleY || 1})`;
            
            return (
              <g key={part} transform={transform}>
                <path
                  d={pathData}
                  onClick={() => handlePartClick(part as HandPart)}
                  onMouseEnter={() => handlePartMouseEnter(part as HandPart)}
                  onMouseLeave={handlePartMouseLeave}
                  stroke="transparent"
                  style={{
                    cursor: mode === 'demonstration' ? 'default' : disabled ? 'not-allowed' : 'pointer',
                    fill: DEBUG_FINGER_AREAS ? 'rgba(255, 0, 0, 0.3)' : 'transparent'
                  }}
                />
              </g>
            );  
          })}
        </svg>
      </div>
    </div>
  );
};

export default LeftHandPicker;
